import moment from 'moment'
import {debounce} from 'lodash'
import {inject, injectable} from 'inversify'
import i18next from 'i18next'
import {action, computed, makeObservable, observable, reaction, runInAction, when} from 'mobx'

import {LoadingState} from '@/types'
import {DEBOUNCE_MS, TableViewType, TABLE_STORAGE_KEYS} from '@/constants'
import {openErrorNotification} from '@/utils'
import {storage} from '@/services/storage'
import {AuthStoreInterface, AuthStoreSymbol, PeriodType} from '@/stores'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {
    PaginationContainerStoreType,
    TRangePickerContainerStore,
    TSelectContainerStore,
    TTabsContainerStore
} from '@/components'

import {IPOSAmexPaymentsStore} from '@/pages/POSAmexPayments'
import {ProfileStoreInterface, ProfileStoreSymbol} from '@/pages/Profile'
import {TDrawerContainerStore} from '@/components/containers/DrawerContainer/TDrawerContainerStore'

import {isFilterSelected} from '@/utils'
import {IFilterContainerStore} from '@/components/containers'
import {IExportsModalContainerStore} from '@/components/containers/transaction'
import {
    POSAmexTransactionRequestType,
    POSAmexTransactionType,
    POSFilterType
} from '@/types/transactions'

import {getPOSAmexTransactions} from './api'
import {
    POSAmexPaymentsDrawerStoreSymbol,
    POSAmexPaymentsExportsStoreSymbol,
    POSAmexPaymentsFilterStoreSymbol,
    POSAmexPaymentsPaginationStoreSymbol,
    POSAmexPaymentsRangePickerStoreSymbol,
    POSAmexPaymentsStatusSelectStoreSymbol,
    POSAmexPaymentsTabsStoreSymbol
} from './symbols'

type F = POSFilterType
type T = POSAmexTransactionType

@injectable()
export class POSAmexPaymentsStore implements IPOSAmexPaymentsStore {
    private readonly _authStore: AuthStoreInterface
    private readonly _profileStore: ProfileStoreInterface

    readonly tabsStore: TTabsContainerStore
    readonly drawerStore: TDrawerContainerStore
    readonly filterStore: IFilterContainerStore<F>
    readonly statusSelectStore: TSelectContainerStore
    readonly rangePickerStore: TRangePickerContainerStore
    readonly paginationStore: PaginationContainerStoreType
    readonly exportsModalStore: IExportsModalContainerStore

    isInfinitePagination = false
    hasMoreData = true

    loadingState = LoadingState.IDLE
    viewType = TableViewType.EXPANDED
    selectedTransaction: T = null

    transactions: T[] = []

    get pageSize() {
        return this.isInfinitePagination ? 50 : this.paginationStore.pageSize
    }

    get request(): POSAmexTransactionRequestType {
        return {
            page: this.paginationStore.pageNumber,
            size: this.pageSize,
            from: this.rangePickerStore.startDate?.format(),
            to: this.rangePickerStore.endDate?.format(),
            ...(isFilterSelected(this.statusSelectStore.value)
                ? {status: this.statusSelectStore.value}
                : {}),
            ...this.filterStore.getFilterForBackend()
        }
    }

    get preferences() {
        return {
            pageSize: this.pageSize,
            from: this.rangePickerStore.startDate,
            to: this.rangePickerStore.endDate,
            status: this.statusSelectStore.value,
            filter: this.filterStore.submittedFilter
        }
    }

    get merchantId() {
        return this._profileStore?.merchant?.merchantId
    }

    get isExportsAvailable() {
        return hasPermissions([
            PermissionsMap.pos_amex_payments.read,
            PermissionsMap.pos_amex_payments.full
        ])
    }

    constructor(
        @inject(AuthStoreSymbol) authStore: AuthStoreInterface,
        @inject(ProfileStoreSymbol) profileStore: ProfileStoreInterface,
        @inject(POSAmexPaymentsTabsStoreSymbol) tabsStore: TTabsContainerStore,
        @inject(POSAmexPaymentsDrawerStoreSymbol) drawerStore: TDrawerContainerStore,
        @inject(POSAmexPaymentsFilterStoreSymbol) filterStore: IFilterContainerStore<F>,
        @inject(POSAmexPaymentsStatusSelectStoreSymbol) statusSelectStore: TSelectContainerStore,
        @inject(POSAmexPaymentsExportsStoreSymbol) exportsModalStore: IExportsModalContainerStore,
        @inject(POSAmexPaymentsRangePickerStoreSymbol) rangePickerStore: TRangePickerContainerStore,
        @inject(POSAmexPaymentsPaginationStoreSymbol) paginationStore: PaginationContainerStoreType
    ) {
        this._authStore = authStore
        this._profileStore = profileStore

        this.tabsStore = tabsStore
        this.filterStore = filterStore
        this.drawerStore = drawerStore
        this.paginationStore = paginationStore
        this.rangePickerStore = rangePickerStore
        this.statusSelectStore = statusSelectStore
        this.exportsModalStore = exportsModalStore

        makeObservable(this, {
            loadingState: observable,
            viewType: observable,
            transactions: observable,
            selectedTransaction: observable,

            request: computed,
            merchantId: computed,
            preferences: computed,

            setViewType: action.bound,
            setSelectedTransaction: action.bound,
            loadTransactions: action.bound
        })

        reaction(
            () => this.preferences,
            debounce(() => {
                if (this.paginationStore.pageNumber === 1) {
                    this.loadTransactions()
                } else {
                    this.paginationStore.setPageNumber(1)
                }
            }, DEBOUNCE_MS)
        )

        reaction(
            () => this.paginationStore.pageNumber,
            () => this.loadTransactions()
        )

        when(
            () => Boolean(this.merchantId) && this.filterStore.loadingState === LoadingState.DONE,
            () => this.initializePreferences()
        )
    }

    private initializePreferences = async () => {
        const profileEmail = storage.get('email')
        const storageKey = `${TABLE_STORAGE_KEYS.posAmexPayments}-${profileEmail}-${this.merchantId}`
        const value = storage.get(storageKey) && JSON.parse(storage.get(storageKey))

        if (value) {
            const {pageSize, period: p, from, to, status, filter} = value
            const period = p as PeriodType

            if (pageSize) {
                this.paginationStore.setPageSize(pageSize)
            }

            if (period && period !== PeriodType.Custom) {
                this.rangePickerStore.setPeriod(period)
            } else if (from && to) {
                this.rangePickerStore.setDates([moment(from), moment(to)])
            }

            if (status) {
                this.statusSelectStore.setValue(status)
            }

            if (filter) {
                this.filterStore.setFilter(filter)
                this.filterStore.submittedFilter = filter
            }
        } else {
            // need to trigger if no filter value in localstorage and reaction in constructor not triggered
            this.loadTransactions()
        }

        reaction(
            () => this.preferences,
            debounce(() => {
                if (profileEmail) {
                    const period = this.rangePickerStore.period
                    storage.set(storageKey, JSON.stringify({...this.preferences, period}))
                }
            }, DEBOUNCE_MS)
        )
    }

    reload = () => {
        if (this.isInfinitePagination && this.paginationStore.pageNumber !== 1) {
            this.paginationStore.setPageNumber(1)
        } else {
            this.loadTransactions()
        }
    }

    async loadTransactions() {
        if (!this._authStore.isAuthenticated || this.loadingState === LoadingState.LOADING) {
            return
        }

        runInAction(() => {
            this.loadingState = LoadingState.LOADING
        })

        const showError = (message: string) => {
            runInAction(() => {
                this.transactions = []
                this.hasMoreData = false
                this.loadingState = LoadingState.FAILED
                this.paginationStore.setTotal(0)
            })

            if (message) {
                openErrorNotification(message)
            }
        }

        try {
            const {result, error} = await getPOSAmexTransactions(this.request)

            if (!error && result) {
                runInAction(() => {
                    const transactions = result.data.map(
                        ({transactionId, returnCodeDescription, ...rest}) => ({
                            ...rest,
                            id: transactionId,
                            description: returnCodeDescription
                        })
                    )

                    if (this.isInfinitePagination && this.paginationStore.pageNumber > 1) {
                        this.transactions = this.transactions.concat(transactions)
                    } else {
                        this.transactions = transactions
                    }

                    this.hasMoreData = transactions.length === this.pageSize
                    this.loadingState = LoadingState.DONE
                    this.paginationStore.setTotal(result.totalCount)
                })
            } else {
                showError(error?.message)
            }
        } catch (error) {
            showError(i18next.t('globals:errors.general'))
        }
    }

    setViewType(viewType: TableViewType) {
        this.viewType = viewType
    }

    setSelectedTransaction(transaction: T) {
        this.selectedTransaction = transaction
    }
}
